import clsx from 'clsx'
import styles from './FilterRail.module.css'
import controls from './controls.module.css'
import DistControl from './DistControl'
import GroupHeader from './GroupHeader'
import PillsControl from './PillsControl'
import RangeControl, { isRangeDegenerate, isRangeEmpty } from './RangeControl'
import ScoreControl from './ScoreControl'
import SearchControl from './SearchControl'
import TiersControl from './TiersControl'
import TogglesControl from './TogglesControl'
import {
  applyParamRef,
  applyRangeRef,
  clearParamGroup,
  type CategoryFilterState,
} from 'lib/dealhunt/filters'
import {
  isScoreFilterGroup,
  type FilterDistGroup,
  type FilterGroup,
  type FilterOption,
  type FilterParamRef,
  type ScoreFilterGroup,
} from 'lib/dealhunt/types'

interface FilterGroupViewProps {
  group: FilterGroup | ScoreFilterGroup
  filters: CategoryFilterState
  onChange: (next: CategoryFilterState) => void
  /** Drawer rows use a lighter header; the rail uses the card treatment. */
  variant?: 'card' | 'row'
}

/** Every distinct param key a group's options can write (extras and `other` included). */
function paramKeysOf(group: FilterGroup | ScoreFilterGroup): FilterParamRef[] {
  if (isScoreFilterGroup(group) || group.control === 'range') {
    return [group.param]
  }
  const refs: FilterParamRef[] = group.options.map(o => o.param)
  if (group.control === 'pills' && group.extras) {
    refs.push(...group.extras.map(o => o.param))
  }
  if (group.control === 'dist' && group.other) {
    refs.push(group.other.param)
  }
  const seen = new Set<string>()
  return refs.filter(r => (seen.has(r.key) ? false : (seen.add(r.key), true)))
}

/** Single-select groups replace their selection; every other control toggles. */
function isSingleSelect(group: FilterGroup | ScoreFilterGroup): boolean {
  return !isScoreFilterGroup(group) && (group.control === 'pillsOne' || group.control === 'tiers')
}

/**
 * The ONE place client-side filter routing lives: an option's intent (`on`) becomes
 * the next filter state. Single-select groups clear their siblings first so the new
 * option replaces rather than adds — including across param keys, which is why this
 * is not just `applyParamRef`. Exported for tests; controls never call it directly.
 */
export function applyGroupOption(
  group: FilterGroup | ScoreFilterGroup,
  filters: CategoryFilterState,
  option: FilterOption,
  on: boolean
): CategoryFilterState {
  const next = isSingleSelect(group) ? clearGroup(group, filters) : filters
  return applyParamRef(next, option.param, on)
}

/** The group card's "Clear": drop every param the group can write. */
export function clearGroup(
  group: FilterGroup | ScoreFilterGroup,
  filters: CategoryFilterState
): CategoryFilterState {
  let next = filters
  for (const ref of paramKeysOf(group)) {
    next = clearParamGroup(next, ref)
  }
  return next
}

/** Past this many options a share bar + top-N rows stops being browsable (78
 *  languages, alphabetical on tied counts, put Thai out of reach): the `dist` group
 *  renders as the Brand typeahead instead. */
export const DIST_TYPEAHEAD_MIN = 12

/** "Find a language — 78 in this category" — the noun is the group's own short label. */
export function distPlaceholder(group: FilterDistGroup): string {
  const noun = (group.short || group.label).toLowerCase()
  return `Find a ${noun} — ${group.options.length} in this category`
}

/** A group with nothing to choose between renders nothing — the client-side twin of
 *  the backend's zero-option drop. Today: a one-value range that is not applied.
 *  Containers that print a heading per section filter on this too. */
export function isGroupHidden(group: FilterGroup | ScoreFilterGroup): boolean {
  return !isScoreFilterGroup(group) && group.control === 'range' && isRangeDegenerate(group)
}

/** One group = label / hint / clear + the control the model asked for. The
 *  control never knows about filter state; it reports option intent and this
 *  view routes it through the `ParamRef` helpers. */
const FilterGroupView: React.VFC<FilterGroupViewProps> = ({
  group,
  filters,
  onChange,
  variant = 'card',
}) => {
  const onOption = (option: FilterOption, on: boolean): void => {
    onChange(applyGroupOption(group, filters, option, on))
  }
  const onRange = (min: number | null, max: number | null): void => {
    if (isScoreFilterGroup(group) || group.control === 'range') {
      onChange(applyRangeRef(filters, group.param, min, max))
    }
  }
  const onClear = (): void => {
    onChange(clearGroup(group, filters))
  }

  if (isGroupHidden(group)) {
    return null
  }

  let control: React.ReactNode
  if (isScoreFilterGroup(group)) {
    control = <ScoreControl group={group} onCommit={min => onRange(min, null)} />
  } else {
    switch (group.control) {
      case 'range':
        control = <RangeControl group={group} onCommit={onRange} />
        break
      case 'pills':
      case 'pillsOne':
        control = <PillsControl group={group} onOption={onOption} />
        break
      case 'toggles':
        control = <TogglesControl group={group} onOption={onOption} />
        break
      case 'tiers':
        control = <TiersControl group={group} onOption={onOption} />
        break
      case 'dist':
        control =
          group.options.length > DIST_TYPEAHEAD_MIN ? (
            <SearchControl
              options={group.options}
              top={group.top}
              placeholder={distPlaceholder(group)}
              other={group.other}
              sentence={group.copy.sentence}
              onOption={onOption}
            />
          ) : (
            <DistControl group={group} onOption={onOption} />
          )
        break
      case 'search':
        control = (
          <SearchControl
            options={group.options}
            top={group.top}
            placeholder={group.placeholder}
            onOption={onOption}
          />
        )
        break
      default:
        control = null
    }
  }

  const hint = isScoreFilterGroup(group) ? null : group.hint
  const empty = !isScoreFilterGroup(group) && group.control === 'range' && isRangeEmpty(group)

  // A yes/no spec arrives as a toggles group with ONE option labelled like the group
  // ("Battery" › "Battery 273"). Every boolean spec does (25/25 on Headphones), so the
  // heading + row pair read as a stutter: collapse to one row — the heading IS the switch.
  const solo =
    !isScoreFilterGroup(group) &&
    group.control === 'toggles' &&
    group.options.length === 1 &&
    group.options[0].label.toLowerCase() === group.label.toLowerCase()
      ? group.options[0]
      : null
  if (solo) {
    return (
      <section
        id={`filter-group-${group.id}`}
        className={clsx(styles.group, {
          [styles.groupActive]: group.active,
          [styles.groupRow]: variant === 'row',
        })}
        aria-label={group.label}
      >
        <button
          type="button"
          className={clsx(styles.groupHead, styles.groupToggle)}
          aria-pressed={solo.selected}
          onClick={() => onOption(solo, !solo.selected)}
        >
          <span className={styles.groupLabel}>
            {group.label}
            <span className={controls.toggleCount}>{solo.count.toLocaleString('en-US')}</span>
          </span>
          <span
            className={clsx(controls.switch, { [controls.switchOn]: solo.selected })}
            aria-hidden="true"
          />
        </button>
      </section>
    )
  }

  return (
    <section
      // Stable per-group anchor (RatingsPanel mirrors it for score axes).
      id={`filter-group-${group.id}`}
      className={clsx(styles.group, {
        [styles.groupActive]: group.active,
        [styles.groupRow]: variant === 'row',
        [styles.groupEmpty]: empty,
      })}
      aria-label={group.label}
    >
      <GroupHeader label={group.label} hint={hint} active={group.active} onClear={onClear} />
      {control}
    </section>
  )
}

export default FilterGroupView
